import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";

// Diagram drawn from x,y % positions on a canvas with subtle starfield.
const Diagram = ({ positions, aspect = "5/4" }) => {
  // Card size relative to canvas
  const cardWidth = positions.length > 8 ? 8.5 : 11; // %
  return (
    <div className="spread-canvas" style={{ aspectRatio: aspect }} data-testid="spread-diagram">
      {/* subtle stars */}
      {Array.from({ length: 28 }).map((_, i) => {
        const x = (i * 53) % 100;
        const y = (i * 89) % 100;
        return <span key={i} className="star" style={{ left: `${x}%`, top: `${y}%` }} />;
      })}

      {positions.map((p) => (
        <div
          key={p.n}
          className="spread-card"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${cardWidth}%`,
            aspectRatio: "2/3",
            transform: `translate(-50%, -50%) rotate(${p.rotate || 0}deg)`,
          }}
          title={p.label}
        >
          {p.n}
        </div>
      ))}
    </div>
  );
};

const SpreadModal = ({ spread, onClose }) => {
  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    onClose();
  };

  return (
    <AnimatePresence>
      {spread && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal-backdrop"
          onClick={onClose}
          data-testid="spread-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-3xl my-10 relative overflow-hidden"
            style={{ background: "#fff", color: "#1f2937", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition"
              style={{ background: "rgba(19,26,72,0.08)" }}
              data-testid="spread-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" style={{ color: "var(--cs-navy)" }} />
            </button>

            <div className="px-8 md:px-12 py-8" style={{ background: "linear-gradient(135deg, var(--cs-purple), var(--cs-rose))", color: "#fff" }}>
              <p className="text-xs uppercase tracking-[0.3em] opacity-90 mb-2">{spread.cards}-card spread</p>
              <h3 className="font-serif-display text-4xl md:text-5xl leading-tight" data-testid="spread-modal-name">{spread.name}</h3>
            </div>

            <div className="p-8 md:p-10 max-h-[72vh] overflow-y-auto">
              <p className="text-lg leading-relaxed mb-5" style={{ color: "#334155" }}>{spread.overview}</p>

              <div className="rounded-2xl p-5 mb-7" style={{ background: "#F1F4FA", border: "1px solid rgba(19,26,72,0.08)" }}>
                <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: "var(--cs-purple)" }}>Best for</p>
                <p style={{ color: "#1f2937" }}>{spread.bestFor}</p>
              </div>

              {/* Diagram */}
              <h4 className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: "var(--cs-blue-dk)" }}>Layout</h4>
              <Diagram positions={spread.positions} aspect={spread.aspect} />

              <h4 className="text-xs uppercase tracking-[0.25em] mb-4 mt-7" style={{ color: "var(--cs-blue-dk)" }}>Card Positions</h4>
              <ol className="space-y-3">
                {spread.positions.map((p) => (
                  <li key={p.n} className="flex gap-4 rounded-2xl p-4" style={{ background: "#F1F4FA" }}>
                    <span className="w-9 h-9 rounded-full flex items-center justify-center font-medium shrink-0 text-white" style={{ background: "linear-gradient(135deg, var(--cs-purple), var(--cs-rose))" }}>
                      {p.n}
                    </span>
                    <div>
                      <p className="font-serif-display text-lg" style={{ color: "var(--cs-navy)" }}>{p.label}</p>
                      <p className="text-sm leading-relaxed" style={{ color: "#475569" }}>{p.meaning}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: "rgba(19,26,72,0.10)" }}>
                <p className="italic font-serif-display text-lg mb-4" style={{ color: "#475569" }}>
                  Curious how this spread reads for you?
                </p>
                <button onClick={scrollTo("contact")} className="btn-pill-primary" data-testid="spread-modal-cta">
                  <Sparkles className="w-4 h-4" /> Book a Reading with Verna
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SpreadModal;
